import { BaseAPI } from 'API/BaseApi';
import { eAPITags } from 'API/eAPITags';
import { IEscrowActualIncomingAchData } from 'types/accounting/data/IEscrowActualIncomingAchData';
import { IEscrowActualIncomingAchQuery } from 'types/accounting/query/IEscrowActualIncomingAchQuery';
import {
  IEscrowActualIncomingAchResponse,
  IEscrowActualIncomingAchsResponse,
} from 'types/accounting/response/IEscrowActualIncomingAchsResponse';
import { IEscrowMatchAchPullDepositsResponse } from 'types/accounting/response/IEscrowExpectedAchPullDepositResponse';
import { IEscrowExpectedIncomingAchData } from 'types/accounting/data/IEscrowExpectedIncomingAchData';
import { IEscrowExpectedIncomingAchQuery } from 'types/accounting/query/IEscrowExpectedIncomingAchQuery';
import {
  convertACHIncomingActualToApiQuery,
  convertACHIncomingExpectedToApiQuery,
  convertAchResponseToData,
  convertActualWireToApiQuery,
  convertCheckActualToApiQuery,
  convertCheckExpectedToApiQuery,
  convertCheckResponseToData,
  convertExpectedAchPullDepositResponseToData,
  convertWireExpectedToApiQuery,
  convertWireResponseToData,
} from './AccountingApi.helpers';
import { IPageData, convertFromIQueryResponseToIPageData } from 'types/rest/IPageData';
import { IEscrowActualIncomingCheckData } from 'types/accounting/data/IEscrowActualIncomingCheckData';
import { IEscrowActualIncomingCheckQuery } from 'types/accounting/query/IEscrowActualIncomingCheckQuery';
import {
  IEscrowActualIncomingCheckResponse,
  IEscrowActualIncomingChecksResponse,
} from 'types/accounting/response/IEscrowActualIncomingChecksResponse';
import { IEscrowExpectedIncomingCheckData } from 'types/accounting/data/IEscrowExpectedIncomingCheckData';
import { IEscrowExpectedIncomingCheckQuery } from 'types/accounting/query/IEscrowExpectedIncomingCheckQuery';
import { IEscrowExpectedIncomingChecksResponse } from 'types/accounting/response/IEscrowExpectedIncomingChecksResponse';
import { IEscrowActualIncomingWireData } from 'types/accounting/data/IEscrowActualIncomingWireData';
import { IEscrowActualIncomingWireQuery } from 'types/accounting/query/IEscrowActualIncomingWireQuery';
import {
  IEscrowActualIncomingWireResponse,
  IEscrowActualIncomingWiresResponse,
} from 'types/accounting/response/IEscrowActualIncomingWiresResponse';
import { IEscrowExpectedIncomingWiresResponse } from 'types/accounting/response/IEscrowExpectedIncomingWiresResponse';
import { IEscrowExpectedIncomingWireData } from 'types/accounting/data/IEscrowExpectedIncomingWireData';
import { IEscrowExpectedIncomingWireQuery } from 'types/accounting/query/IEscrowExpectedIncomingWireQuery';
import { IEscrowBankAccountData } from 'types/accounting/data/IEscrowBankAccountData';

export interface IMatchingProps {
  actualId: string;
  expectedId: string;
}

export const AccountingApi = BaseAPI.injectEndpoints({
  endpoints: (build) => ({
    getACHIncomingActual: build.query<IPageData<IEscrowActualIncomingAchData>, IEscrowActualIncomingAchQuery>({
      query: (query) => ({
        url: `/timber/api/internal/escrow/actual_ach_pull_deposits`,
        params: convertACHIncomingActualToApiQuery(query),
        method: 'GET',
      }),
      transformResponse: (baseReturnValue: IEscrowActualIncomingAchsResponse) =>
        convertFromIQueryResponseToIPageData(baseReturnValue, convertAchResponseToData),
      providesTags: [{ type: eAPITags.IncomingACHActual, id: 'LIST' }],
    }),
    getOneACHIncomingActual: build.query<IEscrowActualIncomingAchData, string>({
      query: (actualId) => ({
        url: `/timber/api/internal/escrow/actual_ach_pull_deposits/${actualId}`,
        method: 'GET',
        params: { includes: ['PARTIES', 'PROPERTY_ADDRESS', 'ACTUAL_ACH_PULL_DEPOSIT', 'MATCHES_ACH_PULL_DEPOSIT'] },
      }),
      transformResponse: (baseReturnValue: IEscrowActualIncomingAchResponse) =>
        convertAchResponseToData(baseReturnValue),
      providesTags: (x, y, id) => [{ type: eAPITags.IncomingACHActual, id }],
    }),
    getACHIncomingExpected: build.query<IPageData<IEscrowExpectedIncomingAchData>, IEscrowExpectedIncomingAchQuery>({
      query: (query) => ({
        url: `/timber/api/internal/escrow/expected-ach-pull-deposits`,
        params: convertACHIncomingExpectedToApiQuery(query),
        method: 'GET',
      }),
      transformResponse: (baseReturnValue: IEscrowMatchAchPullDepositsResponse) =>
        convertFromIQueryResponseToIPageData(baseReturnValue, convertExpectedAchPullDepositResponseToData),
      providesTags: [{ type: eAPITags.IncomingACHExpected, id: 'LIST' }],
    }),
    postMatchACHIncoming: build.mutation<void, IMatchingProps>({
      query: ({ actualId, expectedId }) => ({
        url: `/timber/api/internal/escrow/expected-ach-pull-deposits/${expectedId}/actual-pull-deposits/${actualId}`,
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: (x, y, { actualId: id }) => [
        { type: eAPITags.IncomingACHActual, id: 'LIST' },
        { type: eAPITags.IncomingACHActual, id },
        { type: eAPITags.IncomingACHExpected, id: 'LIST' },
      ],
    }),
    deleteMatchACHIncoming: build.mutation<void, IMatchingProps>({
      query: ({ actualId, expectedId }) => ({
        url: `/timber/api/internal/escrow/expected-ach-pull-deposits/${expectedId}/actual-pull-deposits/${actualId}`,
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: (x, y, { actualId: id }) => [
        { type: eAPITags.IncomingACHActual, id: 'LIST' },
        { type: eAPITags.IncomingACHActual, id },
        { type: eAPITags.IncomingACHExpected, id: 'LIST' },
      ],
    }),
    getChecksIncomingActual: build.query<IPageData<IEscrowActualIncomingCheckData>, IEscrowActualIncomingCheckQuery>({
      query: (query) => ({
        url: `/timber/api/internal/escrow/actual_incoming_checks`,
        params: convertCheckActualToApiQuery(query),
        method: 'GET',
      }),
      transformResponse: (baseReturnValue: IEscrowActualIncomingChecksResponse) =>
        convertFromIQueryResponseToIPageData(baseReturnValue, convertCheckResponseToData),
      providesTags: [{ type: eAPITags.IncomingCheckActual, id: 'LIST' }],
    }),
    getCheckIncomingActual: build.query<IEscrowActualIncomingCheckData, string>({
      query: (actualId) => ({
        url: `/timber/api/internal/escrow/actual_incoming_checks/${actualId}`,
        method: 'GET',
        params: { includes: ['PARTIES', 'PROPERTY_ADDRESS', 'ACTUAL_ACH_PULL_DEPOSIT', 'MATCHES_ACH_PULL_DEPOSIT'] },
      }),
      transformResponse: (baseReturnValue: IEscrowActualIncomingCheckResponse) =>
        convertCheckResponseToData(baseReturnValue),
      providesTags: (x, y, id) => [{ type: eAPITags.IncomingCheckActual, id }],
    }),
    getChecksIncomingExpected: build.query<
      IPageData<IEscrowExpectedIncomingCheckData>,
      IEscrowExpectedIncomingCheckQuery
    >({
      query: (query) => ({
        url: '/timber/api/internal/escrow/expected-incoming-checks',
        params: convertCheckExpectedToApiQuery(query),
        method: 'GET',
      }),
      transformResponse: (baseReturnValue: IEscrowExpectedIncomingChecksResponse) =>
        convertFromIQueryResponseToIPageData(baseReturnValue, (t) => t),
      providesTags: [{ type: eAPITags.IncomingCheckExpected, id: 'LIST' }],
    }),
    postMatchChecksIncoming: build.mutation<void, IMatchingProps>({
      query: ({ actualId, expectedId }) => ({
        url: `/timber/api/internal/escrow/expected-incoming-checks/${expectedId}/actual-incoming-checks/${actualId}`,
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: (x, y, { actualId: id }) => [
        { type: eAPITags.IncomingCheckActual, id: 'LIST' },
        { type: eAPITags.IncomingCheckActual, id },
        { type: eAPITags.IncomingCheckExpected, id: 'LIST' },
      ],
    }),
    deleteMatchChecksIncoming: build.mutation<void, IMatchingProps>({
      query: ({ actualId, expectedId }) => ({
        url: `/timber/api/internal/escrow/expected-incoming-checks/${expectedId}/actual-incoming-checks`,
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: (x, y, { actualId: id }) => [
        { type: eAPITags.IncomingCheckActual, id: 'LIST' },
        { type: eAPITags.IncomingCheckActual, id },
        { type: eAPITags.IncomingCheckExpected, id: 'LIST' },
      ],
    }),
    getWiresIncomingActual: build.query<IPageData<IEscrowActualIncomingWireData>, IEscrowActualIncomingWireQuery>({
      query: (query) => ({
        url: `/timber/api/internal/escrow/actual_incoming_wires`,
        params: convertActualWireToApiQuery(query),
        method: 'GET',
      }),
      transformResponse: (baseReturnValue: IEscrowActualIncomingWiresResponse) =>
        convertFromIQueryResponseToIPageData(baseReturnValue, convertWireResponseToData),
      providesTags: [{ type: eAPITags.IncomingWireActual, id: 'LIST' }],
    }),
    getWireIncomingActual: build.query<IEscrowActualIncomingWireData, string>({
      query: (actualId) => ({
        url: `/timber/api/internal/escrow/actual_incoming_wires/${actualId}`,
        method: 'GET',
        params: { includes: ['PARTIES', 'PROPERTY_ADDRESS', 'ACTUAL_ACH_PULL_DEPOSIT', 'MATCHES_ACH_PULL_DEPOSIT'] },
      }),
      transformResponse: (baseReturnValue: IEscrowActualIncomingWireResponse) =>
        convertWireResponseToData(baseReturnValue),
      providesTags: (x, y, id) => [{ type: eAPITags.IncomingWireActual, id }],
    }),
    getWiresIncomingExpected: build.query<IPageData<IEscrowExpectedIncomingWireData>, IEscrowExpectedIncomingWireQuery>(
      {
        query: (query) => ({
          url: '/timber/api/internal/escrow/expected-incoming-wires',
          params: convertWireExpectedToApiQuery(query),
          method: 'GET',
        }),
        transformResponse: (baseReturnValue: IEscrowExpectedIncomingWiresResponse) =>
          convertFromIQueryResponseToIPageData(baseReturnValue, (t) => t),
        providesTags: [{ type: eAPITags.IncomingWireExpected, id: 'LIST' }],
      }
    ),
    postMatchWiresIncoming: build.mutation<void, IMatchingProps>({
      query: ({ actualId, expectedId }) => ({
        url: `/timber/api/internal/escrow/expected-incoming-wires/${expectedId}/actual-incoming-wires/${actualId}`,
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: (x, y, { actualId }) => [
        { type: eAPITags.IncomingWireActual, id: 'LIST' },
        { type: eAPITags.IncomingWireExpected, id: 'LIST' },
        { type: eAPITags.IncomingWireActual, id: actualId },
      ],
    }),
    deleteMatchWiresIncoming: build.mutation<void, IMatchingProps>({
      query: ({ actualId, expectedId }) => ({
        url: `/timber/api/internal/escrow/expected-incoming-wires/${expectedId}/actual-incoming-wires`,
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: (x, y, { actualId }) => [
        { type: eAPITags.IncomingWireActual, id: 'LIST' },
        { type: eAPITags.IncomingWireExpected, id: 'LIST' },
        { type: eAPITags.IncomingWireActual, id: actualId },
      ],
    }),
    getEscrowBankAccounts: build.query<IEscrowBankAccountData[], void>({
      query: () => ({
        url: '/timber/api/internal/escrow/bank_accounts',
        method: 'GET',
      }),
      providesTags: [{ type: eAPITags.EscrowBankAccounts, id: 'LIST' }],
    }),
  }),
});

export const {
  useGetACHIncomingActualQuery,
  usePostMatchACHIncomingMutation,
  useDeleteMatchACHIncomingMutation,
  useGetACHIncomingExpectedQuery,
  useGetOneACHIncomingActualQuery,
  useGetChecksIncomingActualQuery,
  useGetCheckIncomingActualQuery,
  useGetChecksIncomingExpectedQuery,
  usePostMatchChecksIncomingMutation,
  useDeleteMatchChecksIncomingMutation,
  useGetWiresIncomingActualQuery,
  useGetWireIncomingActualQuery,
  useGetWiresIncomingExpectedQuery,
  usePostMatchWiresIncomingMutation,
  useDeleteMatchWiresIncomingMutation,
  useGetEscrowBankAccountsQuery,
} = AccountingApi;
